import { PRODUCT_NAME } from './branding';

/**
 * Identity for the first-party "Atlas" system bot. Mirrors
 * `bot_id::ATLAS_AI_BOT_ID` on the backend. Atlas is a global system bot
 * available in every channel; mentioning it triggers an AI reply in a thread.
 */
export const ATLAS_AGENT_BOT_ID = '00000000-0000-0000-0000-00000000a1a1';

/**
 * Canonical principal id for Atlas, matching the `bot|<uuid>` form used for
 * bot senders and participants everywhere else. New mentions must always use
 * this form; the bare UUID is only accepted for historical content.
 */
export const ATLAS_AGENT_PRINCIPAL_ID = `bot|${ATLAS_AGENT_BOT_ID}`;

/** Display name for Atlas. */
export const ATLAS_AGENT_NAME = PRODUCT_NAME;

/**
 * User-facing name for Atlas's built-in harness (`in-memory` / `macro-inmem`).
 * Wire slugs stay as they are; this is only what the UI prints.
 */
export const ATLAS_HARNESS_NAME = `${PRODUCT_NAME} Agent`;

/** Handle used to find Atlas in the mention typeahead (`@atlas`). */
export const ATLAS_AGENT_HANDLE = 'atlas';

/**
 * Whether an id refers to the Atlas bot. Accepts both the bare UUID and
 * the `bot|<uuid>` participant/sender form.
 */
export function isAtlasAgentId(id: string | undefined): boolean {
  if (!id) return false;
  const bare = id.startsWith('bot|') ? id.slice('bot|'.length) : id;
  return bare === ATLAS_AGENT_BOT_ID;
}

/**
 * Whether an id is a bot principal: the canonical `bot|<uuid>` form used for
 * bot senders, participants, and mentions, or the Atlas bot's bare UUID
 * (accepted for historical content only).
 */
export function isBotPrincipalId(id: string | undefined): boolean {
  return id?.startsWith('bot|') === true || isAtlasAgentId(id);
}
